import { Suspense } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { FormattedMessage, useIntl } from 'react-intl';

import Clock from '@/assets/icons/clock.svg';
import CurrencyDollar from '@/assets/icons/currency-dollar-green.svg';
import Identification from '@/assets/icons/identification-green.svg';
import Target from '@/assets/icons/target-purple.svg';
import Users from '@/assets/icons/users.svg';
import { CurrencyStats } from '@/components/ui/currency-stats';
import { HireStats } from '@/components/ui/hire-stats';
import { Skeleton } from '@/components/ui/skeleton';
import { Typography } from '@/components/ui/typography';
import { useEditTargetsContext } from '@/contexts/edit-targets-context';
import {
  useOpeningContext,
  useOpeningDetails,
} from '@/contexts/opening-context';
import { useCurrencySymbol } from '@/hooks/use-currency-symbol';
import { useOpeningSlug } from '@/hooks/use-opening-slug';

import { Metric } from './metric';

function AnalyticsContent() {
  const intl = useIntl();
  const { formatCurrency } = useCurrencySymbol();
  const openingSlug = useOpeningSlug();
  const { openEditTargetsDialog } = useEditTargetsContext();
  const { budgetAutomationBannerColor } = useOpeningContext();
  const {
    analyticsData,
    hiringGoal,
    hiringGoalEditUrl,
    opening: { hasActiveHiringGoal },
  } = useOpeningDetails();

  // read-only for past hiring goals
  const isReadOnly = !hiringGoal?.is_current;

  const {
    applicants_stats: applicants,
    hire_stats: hires,
    avg_time_to_hire: avgTimeToHire,
    cpa_stats: cpa,
    budget_stats: budget,
    cost_per_hire: cph,
    conversion_stats: conversion,
    spend_stats: { total: totalSpend, committed: committedSpend },
  } = analyticsData;

  const handleEditTargets = () => {
    openEditTargetsDialog({
      targets: {
        conversion: conversion.expected,
        budget: budget.expected,
        cpa: cpa.expected,
      },
      spendStats: {
        total: totalSpend,
        committed: committedSpend,
      },
    });
  };

  const hideExpectedStats = !hiringGoal?.external_id;

  return (
    <>
      <div className="flex min-h-[84px] flex-row items-start gap-4 overflow-x-auto pb-2">
        <Metric
          label={intl.formatMessage({
            defaultMessage: 'Applicants',
            description:
              'Channel analytics: metric for how many people applied in the period',
          })}
          icon={Users}
        >
          <HireStats
            stats={applicants}
            typographyVariant="large2"
            openingSlug={openingSlug}
            hiringGoalUrl={!hiringGoal ? hiringGoalEditUrl : undefined}
            showOnlyActual={hideExpectedStats}
          />
        </Metric>

        <Metric
          label={intl.formatMessage({
            defaultMessage: 'Hires',
            description:
              'Channel analytics: count of people hired in the period',
          })}
          icon={Identification}
        >
          <HireStats
            stats={hires}
            typographyVariant="large2"
            openingSlug={openingSlug}
            hiringGoalUrl={
              !hiringGoal?.external_id && !hasActiveHiringGoal
                ? hiringGoalEditUrl
                : undefined
            }
            showOnlyActual={!hiringGoal?.external_id && hasActiveHiringGoal}
          />
        </Metric>

        <Metric
          label={intl.formatMessage({
            defaultMessage: 'Budget Spend',
            description:
              'Channel analytics: dollars consumed on this channel’s campaigns',
          })}
          icon={CurrencyDollar}
          color={budgetAutomationBannerColor}
        >
          <CurrencyStats
            stats={budget}
            typographyVariant="large2"
            onClick={isReadOnly ? undefined : handleEditTargets}
            showOnlyActual={hideExpectedStats}
          />
          {totalSpend && committedSpend ? (
            <Typography variant="small1" className="!text-fx-neutral-600 mt-1">
              <FormattedMessage
                defaultMessage="Total spend = {totalSpend} (+{committedSpend} committed)"
                values={{
                  totalSpend: formatCurrency(totalSpend),
                  committedSpend: formatCurrency(committedSpend),
                }}
                description="Breakdown under the budget tile: shows spent and committed spend"
              />
            </Typography>
          ) : null}
        </Metric>

        <Metric
          label={intl.formatMessage({
            defaultMessage: 'CPA',
            description:
              'Channel analytics: cost per applicant (acronym CPA kept short for density)',
          })}
          icon={Target}
        >
          <CurrencyStats
            stats={cpa}
            typographyVariant="large2"
            onClick={isReadOnly ? undefined : handleEditTargets}
            showOnlyActual={hideExpectedStats}
          />
        </Metric>

        <Metric
          label={intl.formatMessage({
            defaultMessage: 'CPH',
            description:
              'Channel analytics: cost per hire (acronym CPH kept short for density)',
          })}
          icon={CurrencyDollar}
        >
          <Typography variant="large2">
            {cph != null ? formatCurrency(cph) : '-'}
          </Typography>
        </Metric>

        <Metric
          label={intl.formatMessage({
            defaultMessage: 'Avg. Time to Hire',
            description:
              'Channel analytics: average days from application to start date',
          })}
          icon={Clock}
        >
          <Typography variant="large2">
            {avgTimeToHire ? (
              <FormattedMessage
                defaultMessage="{count, plural, one {# day} other {# days}}"
                values={{ count: avgTimeToHire }}
                description="Renders a day count with pluralization for the avg hire time row"
              />
            ) : (
              '-'
            )}
          </Typography>
        </Metric>
      </div>
    </>
  );
}

export function Analytics() {
  return (
    <ErrorBoundary fallback={null}>
      <Suspense fallback={<Analytics.Skeleton />}>
        <AnalyticsContent />
      </Suspense>
    </ErrorBoundary>
  );
}

Analytics.Skeleton = function AnalyticsSkeleton() {
  const intl = useIntl();

  return (
    <>
      <div className="flex min-h-[84px] flex-row flex-nowrap items-start gap-4 overflow-x-auto pb-2">
        <Metric
          label={intl.formatMessage({
            defaultMessage: 'Applicants',
            description:
              'Channel analytics: metric for how many people applied in the period',
          })}
          icon={Users}
        >
          <Skeleton className="mt-2 h-4 w-12" />
        </Metric>

        <Metric
          label={intl.formatMessage({
            defaultMessage: 'Hires',
            description:
              'Channel analytics: count of people hired in the period',
          })}
          icon={Identification}
        >
          <Skeleton className="mt-2 h-4 w-12" />
        </Metric>

        <Metric
          label={intl.formatMessage({
            defaultMessage: 'Budget Spend',
            description:
              'Channel analytics: dollars consumed on this channel’s campaigns',
          })}
          icon={CurrencyDollar}
        >
          <Skeleton className="mt-2 h-4 w-12" />
        </Metric>

        <Metric
          label={intl.formatMessage({
            defaultMessage: 'CPA',
            description:
              'Channel analytics: cost per applicant (acronym CPA kept short for density)',
          })}
          icon={Target}
        >
          <Skeleton className="mt-2 h-4 w-12" />
        </Metric>

        <Metric
          label={intl.formatMessage({
            defaultMessage: 'CPH',
            description:
              'Channel analytics: cost per hire (acronym CPH kept short for density)',
          })}
          icon={CurrencyDollar}
        >
          <Skeleton className="mt-2 h-4 w-12" />
        </Metric>

        <Metric
          label={intl.formatMessage({
            defaultMessage: 'Avg. Time to Hire',
            description:
              'Channel analytics: average days from application to start date',
          })}
          icon={Clock}
        >
          <Skeleton className="mt-2 h-4 w-12" />
        </Metric>
      </div>
    </>
  );
};
